import React, { useState, useEffect } from "react";
import {
  Clock,
  Layout,
  CheckCircle2,
  Circle,
  Truck,
  Package,
} from "lucide-react";
import Modal from "../common/CommonModal";
import { useNavigate } from "react-router-dom";

const formatDoDate = (apiDateString) => {
  if (!apiDateString) return "N/A";
  try {
    const date = new Date(apiDateString);
    // Format to MM/DD/YYYY
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    return apiDateString.split("T")[0]; // Fallback to YYYY-MM-DD
  }
};

// Function to format the datetime for the timeline
const formatTimelineDate = (apiDateString) => {
  if (!apiDateString) return "N/A";
  try {
    const date = new Date(apiDateString);
    // Format to MM/DD/YYYY HH:MM AM/PM
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return apiDateString.replace("T", " ");
  }
};

// Function to clean up strings with extra spaces (like ACCOUNT or CUSTOMER)
const cleanString = (str) => {
  return typeof str === "string" ? str.trim() : str;
};

const InvoiceDetailsModal = ({
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  doNo,
  onClose,
}) => {
  const [modalData, setModalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();

  // Fetch data for the modal
  useEffect(() => {
    const fetchData = async () => {
      if (!doNo) return;
      setLoading(true);
      const url = `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANY_CODE}/DDASH_DOPENDING/-/-/-/${doNo}/${DEPARTMENT}/1900-01-01/1900-01-01/-/-/-`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setModalData(data);
        // // Automatically select all rows initially
        // setSelectedRows(data.map((_, index) => index));
      } catch (error) {
        console.error(`Error fetching modal data for DO ${doNo}:`, error);
        setModalData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [doNo, BASE_URL, COMPANY_CODE, DEPARTMENT]);

  const handleCheckboxChange = (index) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(index)
        ? prevSelected.filter((i) => i !== index)
        : [...prevSelected, index]
    );
  };

  const handleTransfer = (sourceType) => {
    if (selectedRows.length === 0) {
      alert("Please select at least one item to transfer.");
      return;
    }

    const transferData = selectedRows.map((index) => modalData[index]);

    navigate("/sales_invoice", {
      state: {
        mode: "new",
        transferredFrom: "DORD",
        transferData,
        shouldFetchTransfer: true,
      },
    });

    onClose();
  };

  return (
    <div className="p-4">
      {loading ? (
        <div className="text-center py-10 text-slate-500">
          Loading DO details...
        </div>
      ) : modalData.length > 0 ? (
        <>
          <div className="overflow-x-auto border rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    Select
                  </th>
                  {[
                    "DO No",
                    "Date",
                    "Customer",
                    "LPO",
                    "Amount",
                    "Salesman",
                  ].map((header) => (
                    <th
                      key={header}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {modalData.map((item, index) => (
                  <tr key={index}>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(index)}
                        onChange={() => handleCheckboxChange(index)}
                        className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded border-gray-300 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {item.do_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {formatDoDate(item.do_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 truncate max-w-xs">
                      {cleanString(item.accdesc)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {item.lpo_no || "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-slate-800">
                      {item.do_total
                        ? Number(item.do_total).toFixed(2)
                        : "0.00"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {item.sale_man}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-lg text-slate-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleTransfer}
              disabled={selectedRows.length === 0}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-95 ${
                selectedRows.length > 0
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Transfer Delivery Order ({selectedRows.length})
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-10 text-slate-500">
          No details found for DO #{doNo}.
        </div>
      )}
    </div>
  );
};
// --- End of New Component ---

const InvoiceGenerationTab = ({
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  onTransfer,
}) => {
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false); // New state for modal
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  // console.log("Selected Delivery", selectedDelivery);
  // console.log("Pending Deee", pendingDeliveries);
  const getTimelineIcon = (status) => {
    switch (status) {
      case "DELIVERED":
        return <CheckCircle2 className="w-4 h-4" />;
      case "ON THE WAY":
        return <Truck className="w-4 h-4" />;
      case "CUSTOMER COLLECT":
        return <Package className="w-4 h-4" />;
      default:
        return <Circle className="w-4 h-4" />;
    }
  };

  //  Fetch Delivery Orders (DO) List
  useEffect(() => {
    const fetchDoList = async () => {
      const url = `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANY_CODE}/DDASH_DOINVLIST/-/-/-/-/${DEPARTMENT}/1900-01-01/1900-01-01/-/-/-`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setPendingDeliveries(data);
      } catch (error) {
        console.error("Error fetching DO list:", error);
      }
    };
    fetchDoList();
  }, [BASE_URL, COMPANY_CODE, DEPARTMENT]);

  // Fetch Timeline Data when a Delivery Order is selected
  useEffect(() => {
    const fetchTimeline = async () => {
      if (!selectedDelivery) {
        setTimelineData([]);
        return;
      }
      const url = `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANY_CODE}/DDASH_DOTIMELINE/-/-/-/${selectedDelivery}/${DEPARTMENT}/1900-01-01/1900-01-01/-/-/-`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setTimelineData(data);
      } catch (error) {
        console.error(
          `Error fetching timeline for DO ${selectedDelivery}:`,
          error
        );
        setTimelineData([]);
      }
    };

    if (showTimeline && selectedDelivery) {
      fetchTimeline();
    } else {
      setTimelineData([]);
    }
  }, [selectedDelivery, showTimeline, BASE_URL, COMPANY_CODE, DEPARTMENT]);

  const getStatusColor = (status) => {
    switch (status) {
      case "DELIVERED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 ring-emerald-500/20";
      case "CUSTOMER COLLECT":
        return "bg-blue-50 text-blue-700 border-blue-200 ring-blue-500/20";
      case "ON THE WAY":
        return "bg-amber-50 text-amber-700 border-amber-200 ring-amber-500/20";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200 ring-gray-500/20";
    }
  };
  const getDoNoId = (doNo) => String(Math.floor(doNo));

  return (
    <div className="flex flex-col bg-gray-50/50 p-2 gap-4 font-sans text-slate-800">
      {/* Header/Controls Section */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-2 rounded-lg border border-gray-100 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">
            Pending Delivery List
            <span className="text-blue-600">
              ({pendingDeliveries?.length ?? 0})
            </span>
          </h2>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border rounded-xl transition-all ${
              showTimeline
                ? "bg-blue-50 text-blue-600 border-blue-200 "
                : "bg-white text-slate-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            <Layout className="w-4 h-4" />
            {showTimeline ? "Hide Timeline" : "Show Timeline"}
          </button>
          <button
            onClick={() => setShowInvoiceModal(true)} // Open the modal on click
            disabled={!selectedDelivery}
            className={`
              px-6 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95
              ${
                selectedDelivery
                  ? "bg-blue-600 hover:bg-blue-700 text-white "
                  : "bg-gray-300 text-gray-500 cursor-not-allowed "
              }
            `}
          >
            Generate Invoice
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 h-full overflow-hidden">
        <div
          className={`flex flex-col gap-4 transition-all duration-300 ease-in-out ${
            showTimeline
              ? "col-span-12 lg:col-span-8 xl:col-span-9"
              : "col-span-12"
          }`}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-gradient-to-r from-slate-100 to-gray-100 border-b border-gray-200 text-sm font-semibold text-gray-600 uppercase tracking-wider">
              <div className="col-span-1">DO NO</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Customer</div>
              <div className="col-span-2">Account</div>
              <div className="col-span-1">LPO</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-1 text-center">Status</div>
            </div>

            <div className="">
              {pendingDeliveries?.length > 0 ? (
                pendingDeliveries?.map((row, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedDelivery(getDoNoId(row.DO_NO))}
                    className={`grid grid-cols-12 gap-3 px-4 py-2.5 items-center cursor-pointer transition-all duration-200 border-l-2 ${
                      String(selectedDelivery) === getDoNoId(row.DO_NO)
                        ? "bg-blue-50 border-l-blue-500"
                        : "hover:bg-slate-50/50 border-l-transparent"
                    }`}
                  >
                    <div
                      className={`col-span-1 font-semibold text-sm ${
                        String(selectedDelivery) === getDoNoId(row.DO_NO)
                          ? "text-blue-600"
                          : "text-slate-700"
                      }`}
                    >
                      {row.DO_NO}
                    </div>
                    <div className="col-span-2 text-sm text-slate-500 whitespace-nowrap">
                      {formatDoDate(row["DO DATE"])}
                    </div>
                    <div
                      className="col-span-3 text-sm font-medium text-slate-700 truncate"
                      title={cleanString(row.CUSTOMER)}
                    >
                      {cleanString(row.CUSTOMER)}
                    </div>
                    <div className="col-span-2 text-sm text-slate-500">
                      {cleanString(row.ACCOUNT)}
                    </div>
                    <div className="col-span-1 text-sm text-slate-400">
                      {row.LPO || "—"}
                    </div>
                    <div className="col-span-2 text-sm text-right text-slate-800 font-semibold">
                      {row.AMOUNT ? Number(row.AMOUNT).toFixed(2) : "0.00"}
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <span
                        className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(
                          row.STATUS
                        )}`}
                      >
                        {row.STATUS}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-slate-400 text-sm">
                  No pending deliveries found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timeline Sidebar */}
        {showTimeline && (
          <div className="col-span-12 lg:col-span-4 xl:col-span-3 flex flex-col h-full animate-in slide-in-from-right-4 fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 h-full flex flex-col overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  Timeline
                </h3>
                {selectedDelivery && (
                  <span className="text-xs font-mono bg-slate-200 px-2 py-0.5 rounded text-slate-600">
                    #{selectedDelivery}
                  </span>
                )}
              </div>

              <div className="p-5 overflow-auto custom-scrollbar flex-1">
                {timelineData?.length > 0 ? (
                  <div className="relative pl-4 border-l-2 border-gray-100 space-y-8">
                    {timelineData.map((item, idx) => (
                      <div key={idx} className="relative pl-2 group">
                        <div
                          className={`absolute -left-[25px] top-1 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center transition-colors ${
                            item.Status === "DELIVERED"
                              ? "bg-emerald-500 text-white shadow-emerald-100"
                              : item.Status === "ON THE WAY"
                              ? "bg-blue-500 text-white shadow-blue-100 animate-pulse"
                              : "bg-gray-200 text-gray-400"
                          } shadow-lg`}
                        >
                          {getTimelineIcon(item.Status)}
                        </div>

                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            {formatTimelineDate(item.Date)}
                          </span>
                          <h4
                            className={`font-bold text-sm ${
                              item.Status === "DELIVERED"
                                ? "text-slate-800"
                                : "text-slate-500"
                            }`}
                          >
                            {item.Status}
                          </h4>
                          {item.HandledBy && (
                            <div className="mt-1 capitalize flex items-center gap-1 text-xs text-slate-400 bg-slate-50 w-fit px-2 py-1 rounded border border-slate-100">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                              {item.HandledBy}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400 text-sm">
                    {selectedDelivery
                      ? "Loading timeline or no events found for this DO."
                      : "Select a Delivery Order to view its timeline."}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Generation Modal */}
      {selectedDelivery && (
        <Modal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          title={`Generate Invoice for DO #${selectedDelivery}`}
          size="xl"
        >
          <InvoiceDetailsModal
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            DEPARTMENT={DEPARTMENT}
            doNo={selectedDelivery}
            onClose={() => setShowInvoiceModal(false)}
            onTransfer={onTransfer}
          />
        </Modal>
      )}
    </div>
  );
};

export default InvoiceGenerationTab;
